import { Component, OnInit, Injector } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { ConfigService } from '../../services/config.service';
import { firstValueFrom } from 'rxjs';
import { SESSION_COOKIE_DAYS } from '../../shared/constants/session';

/*
 * AuthCallbackComponent — finalise le flow SSO sans sortir du domaine PWA.
 *
 * Architecture
 * ------------
 * Authentik redirige le navigateur vers ``https://vendeur.site/auth/callback?code=…&state=…``
 * (le redirect_uri est sur le DOMAINE de la PWA → la PWA standalone reste
 * dans son scope, aucune navigation cross-site n'est nécessaire).
 *
 * Ce composant :
 *  1. Récupère ``code`` et ``state`` depuis la query string.
 *  2. Fait un POST AJAX (``credentials: include`` pour porter le cookie nonce)
 *     vers ``POST /auth/sso/exchange`` côté backend. Le serveur :
 *      - vérifie le state JWT + le cookie ``vd_sso_nonce`` (CSRF),
 *      - échange le code contre les tokens Authentik (server-to-server),
 *      - construit / récupère l'utilisateur applicatif,
 *      - renvoie ``{access_token, refresh_token, user_id, complete_signup, return_to}``.
 *  3. Pose les cookies applicatifs dans ``ngx-cookie-service`` puis navigue
 *     vers ``return_to`` (ou ``/Accueil`` par défaut).
 *
 * Sécurité
 * --------
 * Le ``client_secret`` Authentik n'est JAMAIS exposé au front (l'exchange
 * code↔token se fait côté serveur uniquement). Le ``state`` est un JWT
 * signé HS256 (anti-CSRF) doublé d'un cookie HttpOnly ``vd_sso_nonce``.
 */

@Component({
  selector: 'app-auth-callback',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="callback-page">
      <div class="background-animation">
        <div class="floating-shapes">
          <div class="shape shape-1"></div>
          <div class="shape shape-2"></div>
          <div class="shape shape-3"></div>
        </div>
      </div>
      
      <div class="callback-container">
        <div class="logo-wrapper" [class.error]="hasError">
          <img 
            src="../../../assets/icon-vendeur-transparent-01.png" 
            alt="Vendeur.site Logo" 
            class="logo-icon"
          >
          <div class="logo-glow" [class.error-glow]="hasError"></div>
        </div>
        
        <div class="status-section">
          <div class="loader" *ngIf="!hasError">
            <div class="loader-ring"></div>
            <div class="loader-ring"></div>
            <div class="loader-ring"></div>
          </div>
          
          <div class="error-icon" *ngIf="hasError">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
          </div>
          
          <h2 *ngIf="!hasError">{{ statusMessage }}</h2>
          <h2 *ngIf="hasError" class="error-title">Erreur d'authentification</h2>
          
          <p *ngIf="!hasError" class="status-subtitle">Veuillez patienter...</p>
          <p *ngIf="hasError" class="error-message">{{ errorMessage }}</p>
          
          <button *ngIf="hasError" class="btn-retry" (click)="retryLogin()">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M2.5 2v6h6M21.5 22v-6h-6"/>
              <path d="M22 11.5A10 10 0 0 0 3.2 7.2M2 12.5a10 10 0 0 0 18.8 4.2"/>
            </svg>
            Réessayer
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      --primary: #2563eb;
      --primary-dark: #1d4ed8;
      --secondary: #7c3aed;
      --error: #ef4444;
      --bg-primary: #0f172a;
      --bg-secondary: #1e293b;
    }
    
    .callback-page {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 50%, #0c4a6e 100%);
      position: relative;
      overflow: hidden;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }
    
    .background-animation {
      position: absolute;
      inset: 0;
      overflow: hidden;
      z-index: 0;
    }
    
    .floating-shapes {
      position: absolute;
      inset: 0;
    }
    
    .shape {
      position: absolute;
      border-radius: 50%;
      background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
      opacity: 0.1;
      animation: float 20s infinite ease-in-out;
    }
    
    .shape-1 { width: 400px; height: 400px; top: -100px; left: -100px; }
    .shape-2 { width: 300px; height: 300px; bottom: -50px; right: -80px; animation-delay: -7s; }
    .shape-3 { width: 200px; height: 200px; top: 50%; left: 50%; animation-delay: -14s; }
    
    @keyframes float {
      0%, 100% { transform: translate(0, 0) rotate(0deg); }
      50% { transform: translate(30px, -30px) rotate(180deg); }
    }
    
    .callback-container {
      position: relative;
      z-index: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 3rem;
      background: rgba(255, 255, 255, 0.95);
      backdrop-filter: blur(20px);
      border-radius: 24px;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      animation: slideUp 0.6s ease-out;
    }
    
    @keyframes slideUp {
      from { opacity: 0; transform: translateY(30px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    .logo-wrapper {
      position: relative;
      width: 100px;
      height: 100px;
      margin-bottom: 2rem;
    }
    
    .logo-icon {
      width: 100%;
      height: 100%;
      object-fit: contain;
      position: relative;
      z-index: 2;
      animation: logoPulse 2s ease-in-out infinite;
    }
    
    .logo-wrapper.error .logo-icon {
      animation: none;
      filter: grayscale(50%);
    }
    
    @keyframes logoPulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.05); }
    }
    
    .logo-glow {
      position: absolute;
      inset: -15px;
      background: radial-gradient(circle, rgba(37, 99, 235, 0.4) 0%, transparent 70%);
      border-radius: 50%;
      animation: glowPulse 2s ease-in-out infinite;
    }
    
    .logo-glow.error-glow {
      background: radial-gradient(circle, rgba(239, 68, 68, 0.4) 0%, transparent 70%);
    }
    
    @keyframes glowPulse {
      0%, 100% { opacity: 0.5; transform: scale(1); }
      50% { opacity: 1; transform: scale(1.1); }
    }
    
    .status-section {
      text-align: center;
    }
    
    .loader {
      position: relative;
      width: 60px;
      height: 60px;
      margin: 0 auto 1.5rem;
    }
    
    .loader-ring {
      position: absolute;
      inset: 0;
      border: 3px solid transparent;
      border-top-color: var(--primary);
      border-radius: 50%;
      animation: spin 1.2s linear infinite;
    }
    
    .loader-ring:nth-child(2) {
      inset: 8px;
      border-top-color: var(--secondary);
      animation-duration: 1s;
      animation-direction: reverse;
    }
    
    .loader-ring:nth-child(3) {
      inset: 16px;
      border-top-color: #06b6d4;
      animation-duration: 0.8s;
    }
    
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    
    .error-icon {
      width: 60px;
      height: 60px;
      margin: 0 auto 1.5rem;
      color: var(--error);
      animation: errorShake 0.5s ease-in-out;
    }
    
    .error-icon svg {
      width: 100%;
      height: 100%;
    }
    
    @keyframes errorShake {
      0%, 100% { transform: translateX(0); }
      20%, 60% { transform: translateX(-5px); }
      40%, 80% { transform: translateX(5px); }
    }
    
    h2 {
      font-size: 1.5rem;
      font-weight: 600;
      color: #1e293b;
      margin-bottom: 0.5rem;
    }
    
    .error-title {
      color: var(--error);
    }
    
    .status-subtitle {
      font-size: 0.95rem;
      color: #64748b;
    }
    
    .error-message {
      font-size: 0.95rem;
      color: #64748b;
      max-width: 300px;
      margin-bottom: 1.5rem;
    }
    
    .btn-retry {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 1.5rem;
      font-size: 0.95rem;
      font-weight: 600;
      color: #fff;
      background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
      border: none;
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.3s ease;
      box-shadow: 0 4px 14px rgba(37, 99, 235, 0.4);
    }
    
    .btn-retry:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(37, 99, 235, 0.5);
    }
    
    .btn-retry svg {
      width: 18px;
      height: 18px;
    }
  `]
})
export class AuthCallbackComponent implements OnInit {
  statusMessage: string = 'Finalisation de la connexion…';
  errorMessage: string = '';
  hasError: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private injector: Injector,
    private http: HttpClient,
    private cookieService: CookieService,
  ) {}

  ngOnInit(): void {
    this.handleCallback();
  }

  private async handleCallback(): Promise<void> {
    const qp = this.route.snapshot.queryParamMap;
    const code = qp.get('code');
    const state = qp.get('state');
    const error = qp.get('error');
    const errorDescription = qp.get('error_description');

    if (error) {
      this.showError(errorDescription || `Authentik a retourné une erreur (${error}).`);
      return;
    }
    if (!code || !state) {
      this.showError("Réponse SSO invalide : code ou state manquant.");
      return;
    }

    try {
      this.statusMessage = 'Échange du code…';
      const configService = this.injector.get(ConfigService);
      const apiRoot = (configService.apiUrl || '')
        .replace(/\/+$/, '')
        .replace(/\/authentications$/, '');

      // ─── Bypass Cloudflare WAF cross-origin ──────────────────────
      // Cloudflare déclenche un « Managed Challenge » sur les POST AJAX
      // cross-origin vers vendor-api.codincloud.com (préflight + JSON
      // body sans cf_clearance) → réponse 403 HTML « Just a moment… »
      // sans Access-Control-Allow-Origin → le browser remonte HTTP 0
      // (Unknown Error) côté Angular. Solution : same-origin.
      // Le nginx du vendor_client expose `/auth/sso/exchange` proxié
      // vers vendor_api en réseau overlay, ce qui évite tout aller-
      // retour Internet (et donc Cloudflare).
      const isProdHost = typeof window !== 'undefined'
        && /(vendeur\.site|vendor\.codincloud\.com)$/i.test(window.location.hostname);
      const url = isProdHost
        ? '/auth/sso/exchange'
        : `${apiRoot}/auth/sso/exchange`;

      // ``withCredentials: true`` → indispensable pour que le cookie HttpOnly
      // ``vd_sso_nonce`` posé par ``/auth/sso/start`` soit envoyé au backend
      // (double-check du nonce vs le JWT state).
      const resp: any = await firstValueFrom(
        this.http.post(url, { code, state }, { withCredentials: true })
      );

      if (!resp || !resp.access_token) {
        this.showError('Échange OAuth incomplet (token manquant).');
        return;
      }

      this.statusMessage = 'Connexion réussie, redirection…';

      const cookieOptions = {
        expires: SESSION_COOKIE_DAYS,
        path: '/',
        secure: true,
        sameSite: 'Strict' as const,
      };
      this.cookieService.set('access_token', resp.access_token, cookieOptions);
      if (resp.refresh_token) {
        this.cookieService.set('refresh_token', resp.refresh_token, cookieOptions);
      }
      if (resp.user_id) {
        this.cookieService.set('user_id', String(resp.user_id), cookieOptions);
      }

      const completeSignup = resp.complete_signup === true;
      const returnTo: string = resp.return_to || '/Accueil';
      const safeReturn = this.sanitizeReturnTo(returnTo);
      // Si le compte n'a pas terminé l'onboarding, on force /CompleteSignUp.
      const dest = completeSignup ? safeReturn : '/CompleteSignUp';
      this.router.navigateByUrl(dest);
    } catch (err: any) {
      const detail = err?.error?.detail || err?.message || 'Erreur inconnue';
      console.error('[SSO] exchange failed', err);
      this.showError(`Impossible de finaliser la connexion : ${detail}`);
    }
  }

  /** Anti-open-redirect : on n'autorise que les chemins relatifs. */
  private sanitizeReturnTo(rt: string): string {
    if (!rt || typeof rt !== 'string') return '/Accueil';
    if (rt.startsWith('//') || /:\/\//.test(rt) || rt.startsWith('javascript:')) return '/Accueil';
    if (!rt.startsWith('/')) return '/Accueil';
    return rt;
  }

  private showError(message: string): void {
    this.hasError = true;
    this.errorMessage = message;
  }

  retryLogin(): void {
    this.router.navigate(['/login']);
  }
}
